import React from 'react';
import styled, { css } from 'styled-components';
import { footerLogos } from '../helper/constant';
import { Link } from 'gatsby';
// Images
import Authenticate_Light from '../images/logos/authenticate_light.svg';
import LogoTooltip from './reusable/LogoTooltip';
import CcpaCompliant from '../images/logos/ccpa-compliant-logo.svg';
import GdprCompliant from '../images/logos/gdpr-compliant-logo.svg';
import PrivacyShield from '../images/logos/privacy-shield-framework-logo.svg';

const MainFooter = styled.footer`
    width: 100%;
    background: #0c1a68;
    color: #fff;

    @media screen and (max-width: 600px) {
        padding-bottom: 0px;
    }
`;

const FooterGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 44px;

    @media screen and (max-width: 1024px) {
        grid-template-columns: 100%;
    }
`;

const GridWrapper = styled.div`
    max-width: 1280px;
    margin: 0 auto;
    padding: 72px 20px;

    @media screen and (max-width: 820px) {
        padding: 44px 20px;
    }

    @media screen and (max-width: 500px) {
        padding: 36px 16px;
    }
`;

const LeftSideSection = styled.div`
    margin-bottom: 0;
`;

const RightSideSection = styled.div`
    display: grid;
    grid-template-columns: 1fr 1.5fr 1fr;
    grid-auto-rows: minmax(100px, auto);
    grid-auto-flow: dense;
    gap: 24px;

    @media screen and (max-width: 520px) {
        margin-top: 0px;
        padding: 0;
        grid-auto-flow: row;
        grid-template-columns: 1fr 1.5fr;
        gap: 1.5rem;
    }
`;

const LinkGrid = styled.div`
    width: max-content;

    &:nth-child(4) {
        margin-top: -24px;
    }

    @media screen and (max-width: 520px) {
        &:nth-child(3) {
            order: 1;
            grid-column: span 2;
        }
    }
`;

const ProductGrid = styled.div`
    width: fit-content;

    @media (max-width: 768px) {
        margin-left: 0rem;
    }
`;

const Logo = styled.img`
    width: auto;
    height: 45px;

    ${({ brandLogo }) =>
        brandLogo &&
        css`
            width: 150px;
            height: auto;
        `};

    @media screen and (max-width: 600px) {
        ${({ brandLogo }) =>
            !brandLogo &&
            css`
                transform: scale(0.9);
            `};
    }
`;

const Heading = styled.h1`
    font-size: 16px;
    font-weight: 500;
    line-height: 18.96px;
    color: #fff;
    text-transform: uppercase;
    margin-bottom: 12px;
`;

const SubText = styled.p`
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    margin-top: 20px;
    color: #e5e7eb;
    width: 70%;

    @media screen and (max-width: 1024px) {
        width: 100%;
    }
`;

const sharedLinkStyle = css`
    text-decoration: none;
    font-size: 14px;
    font-weight: 400;
    line-height: 31.92px;
    color: #d1d5db;
    display: block;

    ${({ address }) =>
        address &&
        css`
            line-height: 19.6px;
            margin-top: ${({ marginTop }) => marginTop};
        `};
`;

const NavLinks = styled.a`
    ${sharedLinkStyle}
`;

const RedirectLink = styled(Link)`
    ${sharedLinkStyle}
`;

const LogoContainer = styled.div`
    margin-top: 40px;
    display: flex;
    flex-wrap: wrap;
    row-gap: 40px;
    column-gap: 20px;
    width: 90%;

    @media screen and (max-width: 1024px) {
        column-gap: 24px;
        width: 100%;
    }

    @media screen and (max-width: 500px) {
        column-gap: 20px;
        row-gap: 24px;
    }
`;

const CompliantLogo = styled.img`
    transform: scale(0.99);
`;

const Lines = styled.div`
    border-top: 1px solid rgba(249, 250, 251, 0.5);
    margin-top: 24px;
`;

const CopyrightText = styled.p`
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    color: #eff8ff;
    margin-top: 8px;
`;

function Footer() {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();

    return (
        <MainFooter>
            <GridWrapper>
                <FooterGrid>
                    <LeftSideSection>
                        <Logo
                            brandLogo
                            src={Authenticate_Light}
                            alt="Authenticate.com"
                            width="auto"
                            height="auto"
                            loading="lazy"
                        />
                        <CopyrightText>
                            {`Copyright © ${currentYear} Authenticating.com LLC. All rights reserved.`}
                        </CopyrightText>
                        <SubText>
                            Disclaimer : Authenticating.com LLC d/b/a Authenticate, is not a
                            consumer reporting agency & its service does not constitute "consumer
                            reporting" as such term is defined in the federal Fair Credit Reporting
                            Act (FCRA), 15 U.S.C.A. sec 1681 et seq. The data provided from this
                            service may not be used as a factor in establishing a consumer's
                            eligibility for credit, insurance, employment or for any other purpose
                            in the FCRA & you agree not to use this service for such a purpose or to
                            take adverse action regarding any consumer application based on this
                            service.
                        </SubText>
                        <LogoContainer>
                            {footerLogos.map((logo) => (
                                <NavLinks
                                    address={true}
                                    target="_blank"
                                    rel="noreferrer"
                                    href={logo.link}
                                    key={logo.id}
                                >
                                    {logo.tooltipText ? (
                                        <LogoTooltip content={logo.tooltipText}>
                                            <Logo
                                                src={logo.logo}
                                                alt={logo.name}
                                                margin
                                                width="auto"
                                                height="auto"
                                                loading="lazy"
                                            />
                                        </LogoTooltip>
                                    ) : (
                                        <Logo
                                            src={logo.logo}
                                            alt={logo.name}
                                            margin
                                            width="auto"
                                            height="auto"
                                            loading="lazy"
                                        />
                                    )}
                                </NavLinks>
                            ))}
                            <CompliantLogo
                                src={CcpaCompliant}
                                alt="ccpa_compliant"
                                margin
                                width="auto"
                                height="auto"
                                loading="lazy"
                            />
                            <CompliantLogo
                                src={GdprCompliant}
                                alt="gdpr_compliant"
                                margin
                                width="auto"
                                height="auto"
                                loading="lazy"
                            />
                            <CompliantLogo
                                src={PrivacyShield}
                                alt="privacy_shield_framework"
                                margin
                                width="auto"
                                height="auto"
                                loading="lazy"
                            />
                        </LogoContainer>
                    </LeftSideSection>
                    <RightSideSection>
                        <LinkGrid>
                            <Heading>Navigation</Heading>
                            {/* when accessing link from footer it will scroll to bottom of the page that is fixed by using gatsby Link instead of anchor tag */}
                            <RedirectLink to="/">Home</RedirectLink>
                            <RedirectLink to="/#solution">Solutions</RedirectLink>
                            <RedirectLink to="/#pricing">Pricing</RedirectLink>
                            <RedirectLink to="/#contact">Contact Us</RedirectLink>
                            <Lines />
                        </LinkGrid>
                        <ProductGrid>
                            <Heading>Products</Heading>
                            <RedirectLink to="/products/identity/">
                                Identity Verification
                            </RedirectLink>
                            <RedirectLink to="/products/criminal-background-checks/">
                                Criminal Background Checks
                            </RedirectLink>
                            <RedirectLink to="/products/background-check/">
                                Background Verifications
                            </RedirectLink>
                            <RedirectLink to="/products/tcm/">
                                True Continuous Monitoring™
                            </RedirectLink>
                            <RedirectLink to="/products/reauthenticate/">
                                ReAuthenticate™
                            </RedirectLink>
                            <Lines />
                        </ProductGrid>
                        <LinkGrid>
                            <Heading>Resources</Heading>
                            <NavLinks
                                target="_blank"
                                rel="noreferrer"
                                href="https://docs.authenticate.com/"
                            >
                                Documentation
                            </NavLinks>
                            <RedirectLink to="/resources/blog">Blog</RedirectLink>
                            <NavLinks
                                target="_blank"
                                rel="noreferrer"
                                href="https://info.authenticate.com/"
                            >
                                Knowledge Base
                            </NavLinks>
                            <RedirectLink to="/resources/security-and-compliance">
                                Security and Compliance
                            </RedirectLink>
                        </LinkGrid>
                        <LinkGrid>
                            <Heading>Links</Heading>
                            <RedirectLink target="_blank" rel="noreferrer" to="/terms-of-use">
                                {' '}
                                Terms of Use
                            </RedirectLink>
                            <RedirectLink target="_blank" rel="noreferrer" to="/privacy-policy">
                                Privacy Policy
                            </RedirectLink>
                            <NavLinks
                                target="_blank"
                                rel="noreferrer"
                                href="https://portal.authenticate.com/register"
                            >
                                Get Started
                            </NavLinks>
                            <NavLinks
                                target="_blank"
                                rel="noreferrer"
                                href="https://verify.authenticating.com/onboarding"
                            >
                                Medallion™
                            </NavLinks>
                            <NavLinks
                                target="_blank"
                                rel="noreferrer"
                                href="https://apply.workable.com/authenticate"
                            >
                                Careers
                            </NavLinks>
                            <NavLinks
                                target="_blank"
                                rel="noreferrer"
                                href="https://statuspage.freshping.io/48329-Authenticating"
                            >
                                System Status
                            </NavLinks>
                        </LinkGrid>
                        <LinkGrid>
                            <Heading>Contact</Heading>
                            <NavLinks href="mailto:info@authenticate.com" address={true}>
                                Email: <br />
                                info@authenticate.com
                            </NavLinks>
                            <NavLinks marginTop="10px" address={true}>
                                Toll Free: <br /> (833) 2-VERIFY
                            </NavLinks>
                            <NavLinks marginTop="10px" address={true}>
                                730 Arizona Avenue, Suite 230,
                                <br /> Santa Monica, CA 90401
                            </NavLinks>
                        </LinkGrid>
                    </RightSideSection>
                </FooterGrid>
            </GridWrapper>
        </MainFooter>
    );
}

export default Footer;
